import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 30. COMMUNICATION
// Système Communication central réutilisé par CRM, Support, Booking,
// Sales et Marketing. Supporte progressivement : messages internes,
// email, WhatsApp, SMS, notifications. Chaque conversation peut être
// reliée à une entité : Customer, Business, Order, Booking,
// Appointment, Ticket.
// ─────────────────────────────────────────────────────────────

export type ConversationChannel = 'internal' | 'email' | 'whatsapp' | 'sms' | 'notification' | 'ai_chat';

export const COMM_CHANNELS: {
  key: ConversationChannel;
  label: string;
  icon: string;
  available: boolean;
  className: string;
}[] = [
  { key: 'internal', label: 'Message interne', icon: 'ri-chat-3-line', available: true, className: 'bg-secondary-100 text-secondary-900' },
  { key: 'ai_chat', label: 'Assistant IA', icon: 'ri-robot-2-line', available: true, className: 'bg-accent-100 text-accent-900' },
  { key: 'email', label: 'Email', icon: 'ri-mail-line', available: true, className: 'bg-primary-100 text-primary-900' },
  { key: 'whatsapp', label: 'WhatsApp', icon: 'ri-whatsapp-line', available: true, className: 'bg-accent-100 text-accent-900' },
  { key: 'notification', label: 'Notification', icon: 'ri-notification-3-line', available: true, className: 'bg-background-100 text-foreground-700' },
  { key: 'sms', label: 'SMS', icon: 'ri-message-2-line', available: false, className: 'bg-background-100 text-foreground-500' },
];

export function channelMeta(key: string | null | undefined) {
  return COMM_CHANNELS.find((c) => c.key === key) || COMM_CHANNELS[0];
}

export const CONVERSATION_ENTITY_TYPES: { key: string; label: string; icon: string }[] = [
  { key: 'customer', label: 'Client', icon: 'ri-user-star-line' },
  { key: 'ai_client', label: 'Client (Assistant IA)', icon: 'ri-robot-2-line' },
  { key: 'business', label: 'Entreprise', icon: 'ri-building-2-line' },
  { key: 'order', label: 'Commande', icon: 'ri-shopping-bag-3-line' },
  { key: 'booking', label: 'Réservation', icon: 'ri-calendar-check-line' },
  { key: 'appointment', label: 'Rendez-vous', icon: 'ri-calendar-line' },
  { key: 'ticket', label: 'Ticket', icon: 'ri-coupon-3-line' },
];

export function conversationEntityMeta(key: string | null | undefined) {
  return CONVERSATION_ENTITY_TYPES.find((e) => e.key === key) || null;
}

export const CONVERSATION_STATUSES: { key: string; label: string; className: string }[] = [
  { key: 'open', label: 'Ouverte', className: 'bg-primary-100 text-primary-900' },
  { key: 'pending', label: 'En attente', className: 'bg-accent-100 text-accent-900' },
  { key: 'closed', label: 'Fermée', className: 'bg-background-100 text-foreground-600' },
];

export function conversationStatusMeta(key: string | null | undefined) {
  return CONVERSATION_STATUSES.find((s) => s.key === key) || CONVERSATION_STATUSES[0];
}

export interface CoreConversation {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  channel: string;
  subject: string | null;
  entity_type: string | null;
  entity_id: string | null;
  contact_id: number | null;
  customer_id: number | null;
  assigned_user_id: number | null;
  status: string;
  unread_count: number | null;
  last_message_at: string | null;
  metadata: Record<string, unknown> | null;
  deleted_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface CoreConversationMessage {
  id: number;
  conversation_id: number;
  direction: string | null;
  channel: string | null;
  author_type: string | null;
  author_user_id: number | null;
  author_name: string | null;
  body: string | null;
  attachments: unknown;
  is_internal: boolean | null;
  created_at: string | null;
}

export async function listCoreConversations(organizationId: number): Promise<CoreConversation[]> {
  const { data, error } = await supabase
    .from('core_conversations')
    .select('*')
    .eq('organization_id', organizationId)
    .is('deleted_at', null)
    .order('last_message_at', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as CoreConversation[];
}

export async function listConversationsForEntity(
  organizationId: number,
  entityType: string,
  entityId: string | number,
): Promise<CoreConversation[]> {
  const { data, error } = await supabase
    .from('core_conversations')
    .select('*')
    .eq('organization_id', organizationId)
    .eq('entity_type', entityType)
    .eq('entity_id', String(entityId))
    .is('deleted_at', null)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as CoreConversation[];
}

export interface ConversationInput {
  tenantId: number | null;
  organizationId: number;
  channel: ConversationChannel;
  subject: string;
  entityType?: string | null;
  entityId?: string | null;
  contactId?: number | null;
  customerId?: number | null;
  assignedUserId?: number | null;
}

export async function createCoreConversation(input: ConversationInput): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_conversations')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      channel: input.channel,
      subject: input.subject,
      entity_type: input.entityType || null,
      entity_id: input.entityId || null,
      contact_id: input.contactId ?? null,
      customer_id: input.customerId ?? null,
      assigned_user_id: input.assignedUserId ?? null,
      status: 'open',
      unread_count: 0,
      last_message_at: new Date().toISOString(),
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function updateCoreConversation(
  id: number,
  patch: { subject?: string; status?: string; assignedUserId?: number | null; channel?: ConversationChannel },
): Promise<void> {
  const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.subject !== undefined) payload.subject = patch.subject;
  if (patch.status !== undefined) payload.status = patch.status;
  if (patch.assignedUserId !== undefined) payload.assigned_user_id = patch.assignedUserId ?? null;
  if (patch.channel !== undefined) payload.channel = patch.channel;
  const { error } = await supabase.from('core_conversations').update(payload).eq('id', id);
  if (error) throw error;
}

export async function softDeleteCoreConversation(id: number, userId?: number | null): Promise<void> {
  const { error } = await supabase
    .from('core_conversations')
    .update({ deleted_at: new Date().toISOString(), deleted_by: userId ?? null })
    .eq('id', id);
  if (error) throw error;
}

export async function restoreCoreConversation(id: number): Promise<void> {
  const { error } = await supabase
    .from('core_conversations')
    .update({ deleted_at: null, deleted_by: null })
    .eq('id', id);
  if (error) throw error;
}

export async function listConversationMessages(conversationId: number): Promise<CoreConversationMessage[]> {
  const { data, error } = await supabase
    .from('core_conversation_messages')
    .select('*')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []) as CoreConversationMessage[];
}

/** Mots vides ignorés pour retrouver le message concerné par une demande. */
const QUERY_STOPWORDS = new Set([
  'dans', 'pour', 'avec', 'chez', 'quel', 'quelle', 'quels', 'quelles',
  'est', 'sont', 'une', 'des', 'les', 'mon', 'mes', 'ton', 'tes', 'son',
  'ses', 'que', 'qui', 'quoi', 'comment', 'pourquoi', 'plus', 'très',
  'bonjour', 'salut', 'merci', 'stp', 'svp', 'moi', 'toi', 'nous', 'vous',
]);

/**
 * Charge UNIQUEMENT les messages d'un fil qui correspondent à une demande
 * précise (recherche plein texte sur le corps des messages).
 *
 * Utilisé par la vue marchand « demande liée à mon offre » : le commerçant
 * voit la demande qui le concerne, jamais le reste de la conversation privée
 * du client avec l'assistant. Le filtrage est fait côté base, donc les autres
 * messages ne quittent pas le serveur.
 */
export async function listConversationMessagesForQuery(
  conversationId: number,
  queryText: string | null | undefined,
  limit = 4,
): Promise<CoreConversationMessage[]> {
  if (!conversationId) return [];
  const terms = String(queryText || '')
    .toLowerCase()
    .replace(/[«»"'?!.,;:()]/g, ' ')
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length >= 4 && !QUERY_STOPWORDS.has(t))
    .slice(0, 6);

  try {
    if (terms.length === 0) {
      const { data } = await supabase
        .from('core_conversation_messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .eq('author_type', 'customer')
        .order('created_at', { ascending: false })
        .limit(1);
      return (data || []) as CoreConversationMessage[];
    }
    const filter = terms
      .map((t) => t.replace(/[%,()*\\]/g, ' ').trim())
      .filter(Boolean)
      .map((t) => `body.ilike.%${t}%`)
      .join(',');
    const { data } = await supabase
      .from('core_conversation_messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .eq('author_type', 'customer')
      .or(filter)
      .order('created_at', { ascending: false })
      .limit(limit);
    return (data || []) as CoreConversationMessage[];
  } catch {
    return [];
  }
}

export interface MessageInput {
  conversationId: number;
  tenantId: number | null;
  organizationId: number;
  channel?: string;
  direction?: string;
  authorType?: string;
  authorUserId?: number | null;
  authorName?: string | null;
  body: string;
  isInternal?: boolean;
}

export async function createConversationMessage(input: MessageInput): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_conversation_messages')
    .insert({
      conversation_id: input.conversationId,
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      direction: input.direction || 'outbound',
      channel: input.channel || 'internal',
      author_type: input.authorType || 'agent',
      author_user_id: input.authorUserId ?? null,
      author_name: input.authorName ?? null,
      body: input.body,
      attachments: [],
      is_internal: !!input.isInternal,
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  await supabase
    .from('core_conversations')
    .update({ last_message_at: new Date().toISOString(), updated_at: new Date().toISOString() })
    .eq('id', input.conversationId);
  return (data?.id as number) ?? null;
}

export interface ConversationStats {
  total: number;
  byChannel: Record<string, number>;
  byStatus: Record<string, number>;
  open: number;
}

export async function fetchConversationStats(organizationId: number): Promise<ConversationStats> {
  const { data } = await supabase
    .from('core_conversations')
    .select('channel, status')
    .eq('organization_id', organizationId)
    .is('deleted_at', null);
  const byChannel: Record<string, number> = {};
  const byStatus: Record<string, number> = {};
  let open = 0;
  (data || []).forEach((r) => {
    const row = r as { channel: string | null; status: string | null };
    const ch = row.channel || 'internal';
    byChannel[ch] = (byChannel[ch] || 0) + 1;
    const st = row.status || 'open';
    byStatus[st] = (byStatus[st] || 0) + 1;
    if (st === 'open') open += 1;
  });
  return { total: (data || []).length, byChannel, byStatus, open };
}

export interface AiClientThreadSummary {
  conversationId: number;
  clientId: number;
  subject: string | null;
  status: string;
  lastMessageAt: string | null;
}

/**
 * Liste les fils IA client pertinents pour une organisation, déduits des
 * notifications « Recherche client IA » reçues. Ainsi CHAQUE marchand concerné
 * peut rouvrir et répondre au fil, même si la conversation est rattachée à une
 * autre organisation.
 */
export async function listAiClientThreadsForOrganization(
  organizationId: number,
): Promise<AiClientThreadSummary[]> {
  const { data: notifs } = await supabase
    .from('core_notifications')
    .select('entity_id, created_at')
    .eq('organization_id', organizationId)
    .eq('type', 'ai_customer_search')
    .order('created_at', { ascending: false })
    .limit(200);

  const clientIds = Array.from(new Set(
    (notifs || [])
      .map((n) => Number((n as { entity_id: string | null }).entity_id))
      .filter((n) => Number.isFinite(n) && n > 0),
  ));
  if (clientIds.length === 0) return [];

  const { data: convs } = await supabase
    .from('core_conversations')
    .select('id, entity_id, subject, status, last_message_at')
    .eq('entity_type', 'ai_client')
    .in('entity_id', clientIds.map(String))
    .is('deleted_at', null);

  const byClient: Record<number, AiClientThreadSummary> = {};
  (convs || []).forEach((c) => {
    const row = c as { id: number; entity_id: string | null; subject: string | null; status: string | null; last_message_at: string | null };
    const cid = Number(row.entity_id);
    if (!Number.isFinite(cid) || cid <= 0) return;
    const ts = row.last_message_at ? new Date(row.last_message_at).getTime() : 0;
    const existing = byClient[cid];
    const existingTs = existing?.lastMessageAt ? new Date(existing.lastMessageAt).getTime() : 0;
    if (!existing || ts > existingTs) {
      byClient[cid] = {
        conversationId: row.id,
        clientId: cid,
        subject: row.subject,
        status: row.status || 'open',
        lastMessageAt: row.last_message_at,
      };
    }
  });

  return Object.values(byClient).sort((a, b) =>
    (b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0) -
    (a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0),
  );
}

/**
 * Ouvre (ou réutilise) une conversation reliée à une entité métier.
 * Utilisé par les modules (Booking, Sales, Support…) pour émettre
 * automatiquement un message dans le système Communication central.
 */
export async function ensureEntityConversation(input: {
  tenantId: number | null;
  organizationId: number;
  entityType: string;
  entityId: string | number;
  channel: ConversationChannel;
  subject: string;
  customerId?: number | null;
  contactId?: number | null;
  initialMessage?: string;
  authorName?: string | null;
}): Promise<number | null> {
  try {
    const existing = await listConversationsForEntity(input.organizationId, input.entityType, input.entityId);
    let convId = existing[0]?.id ?? null;
    if (!convId) {
      convId = await createCoreConversation({
        tenantId: input.tenantId,
        organizationId: input.organizationId,
        channel: input.channel,
        subject: input.subject,
        entityType: input.entityType,
        entityId: String(input.entityId),
        customerId: input.customerId ?? null,
        contactId: input.contactId ?? null,
      });
    }
    if (convId && input.initialMessage) {
      await createConversationMessage({
        conversationId: convId,
        tenantId: input.tenantId,
        organizationId: input.organizationId,
        channel: input.channel,
        direction: 'inbound',
        authorType: 'system',
        authorName: input.authorName ?? 'Système',
        body: input.initialMessage,
      });
    }
    return convId;
  } catch {
    return null;
  }
}